import {
  createRouter,
  createRoute,
  createRootRoute,
  Outlet,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { useThemeStore, applyTheme } from "@/stores/theme-store";
import { useAuthStore } from "@/stores/auth-store";
import LoginPage from "@/features/auth/LoginPage";
import CallbackPage from "@/features/auth/CallbackPage";
import AdminLayout from "@/features/admin/AdminLayout";
import AdminDashboardPage from "@/features/admin/AdminDashboardPage";
import TeamsPage from "@/features/teams/TeamsPage";
import ProjectsPage from "@/features/projects/ProjectsPage";
import UsersPage from "@/features/users/UsersPage";
import APIKeysPage from "@/features/api-keys/APIKeysPage";
import RoutesPage from "@/features/routing/RoutesPage";
import PoliciesPage from "@/features/policies/PoliciesPage";
import SettingsPage from "@/features/settings/SettingsPage";
import NotificationsPage from "@/features/notifications/NotificationsPage";
import SSOOriginsPage from "@/features/sso-origins/SSOOriginsPage";

// Root route with theme application
const rootRoute = createRootRoute({
  component: () => {
    const theme = useThemeStore((state) => state.theme);

    useEffect(() => {
      applyTheme(theme);
    }, [theme]);

    return <Outlet />;
  },
});

// Index route - redirect based on auth
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: () => {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

    useEffect(() => {
      if (isAuthenticated) {
        window.location.href = "/admin";
      } else {
        window.location.href = "/login";
      }
    }, [isAuthenticated]);

    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
      </div>
    );
  },
});

// Auth routes (no layout)
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/callback",
  component: CallbackPage,
});

// Admin layout route
const adminLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  component: AdminLayout,
});

// Admin child routes
const adminDashboardRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/",
  component: AdminDashboardPage,
});

const teamsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/teams",
  component: TeamsPage,
});

const projectsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/projects",
  component: ProjectsPage,
});

const usersRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/users",
  component: UsersPage,
});

const apiKeysRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/api-keys",
  component: APIKeysPage,
});

const routesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/routes",
  component: RoutesPage,
});

const policiesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/policies",
  component: PoliciesPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/settings",
  component: SettingsPage,
});

const notificationsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/notifications",
  component: NotificationsPage,
});

const ssoOriginsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/sso-origins",
  component: SSOOriginsPage,
});

// Create route tree
const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  callbackRoute,
  adminLayoutRoute.addChildren([
    adminDashboardRoute,
    teamsRoute,
    projectsRoute,
    usersRoute,
    apiKeysRoute,
    routesRoute,
    policiesRoute,
    notificationsRoute,
    ssoOriginsRoute,
    settingsRoute,
  ]),
]);

// Create router
export const router = createRouter({ routeTree });

// Register types
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
